'use client';

import { Award, Newspaper, Quote, Star } from 'lucide-react';

const DISTINCTIONS = [
  { year: '2018', title: 'Prix de l\'Artisanat d\'Exception', org: 'Made in Brussels Fair', text: 'Le sac « Signature Cognac » reçoit la distinction suprême pour son innovation dans la couture sellier.' },
  { year: '2020', title: 'Maître Artisan d\'Europe', org: 'Fédération Européenne des Métiers', text: 'Reconnaissance du savoir-faire transfrontalier Dakar-Anvers décernée à S.E. MATAR.' },
  { year: '2022', title: 'Best of Belgian Craft', org: 'Wallonia-Brussels Heritage', text: 'Sélection parmi les dix maisons belges perpétuant un métier d\'art menacé.' },
  { year: '2024', title: 'Lauréat Ville Dorée', org: 'Smartest Capital', text: 'Intégration au cercle prestigieux des maisons de la « Ville Dorée » panafricaine.' },
];

const PRESS = [
  { source: 'Le Soir', title: 'L\'or noir d\'Anvers : rencontre avec MATAR', year: '2024', excerpt: 'Une maison qui réinvente la grammaire du cuir, entre Dakar et la Meir.' },
  { source: 'Vogue Belgique', title: 'Ces artisans qui font le luxe belge', year: '2023', excerpt: 'MATAR STYLE CUIR impose sa patine singulière, à mi-chemin entre héritage et désir.' },
  { source: 'De Standaard', title: 'Cuir et conscience — l\'éthique de MATAR', year: '2024', excerpt: 'Le luxe responsable, un credo que la maison d\'Anvers défend sans relâche.' },
  { source: 'Jeune Afrique', title: 'La diaspora qui fait le luxe européen', year: '2023', excerpt: 'Comment S.E. MATAR a fait dialoguer deux rives et deux savoir-faire millénaires.' },
];

const CLIENTS = [
  { value: '10 000+', label: 'Clients confidents' },
  { value: '12 ans', label: 'D\'atelier à Anvers' },
  { value: '48', label: 'Distinctions reçues' },
  { value: '100%', label: 'Cuir véritable' },
];

export default function Distinctions() {
  return (
    <section
      id="distinctions"
      className="relative py-24 sm:py-32 bg-[#0a0a0f] border-t border-[#D4AF37]/10 overflow-hidden"
    >
      {/* Background image with heavy overlay */}
      <div className="absolute inset-0">
        <img
          src="/images/distinctions.png"
          alt="Trophées et distinctions de la maison MATAR"
          className="w-full h-full object-cover"
          style={{ filter: 'brightness(0.25) saturate(1.1)' }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,10,15,0.88) 0%, rgba(10,10,15,0.7) 50%, rgba(10,10,15,0.92) 100%)',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="block h-px w-10 bg-[#D4AF37]" />
            <span className="eyebrow">Distinctions & Presse</span>
            <span className="block h-px w-10 bg-[#D4AF37]" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
            <span className="text-[#F4E8C1]">Reconnue par les</span>{' '}
            <span className="text-gold-gradient">meilleurs.</span>
          </h2>
          <p className="max-w-2xl mx-auto text-[#F4E8C1]/65 font-light leading-relaxed">
            Douze ans d'atelier, quarante-huit distinctions, une presse qui
            salue l'exigence. La maison n'a jamais cherché la reconnaissance :
            c'est elle qui est venue vers nous.
          </p>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-20">
          {CLIENTS.map((c) => (
            <div
              key={c.label}
              className="text-center bg-[#12121a]/60 backdrop-blur-sm border border-[#D4AF37]/20 p-6"
            >
              <div className="font-display text-3xl sm:text-4xl font-bold text-gold-gradient">
                {c.value}
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#F4E8C1]/60 mt-2">
                {c.label}
              </div>
            </div>
          ))}
        </div>

        {/* Two columns: Awards + Press */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14">
          {/* Awards */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Award size={20} className="text-[#D4AF37]" />
              <h3 className="font-display text-xl font-semibold text-[#F4E8C1] tracking-wide uppercase">
                Distinctions
              </h3>
            </div>
            <div className="space-y-4">
              {DISTINCTIONS.map((d) => (
                <div
                  key={d.title}
                  className="bg-[#12121a]/60 backdrop-blur-sm border border-[#D4AF37]/20 p-5 hover:border-[#D4AF37]/50 transition-colors duration-400 group"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-14 text-center">
                      <div className="font-display text-xl font-bold text-gold-gradient">
                        {d.year}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Star size={12} className="text-[#D4AF37] fill-[#D4AF37]" />
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/85">
                          {d.org}
                        </span>
                      </div>
                      <h4 className="font-display text-base font-semibold text-[#F4E8C1] mb-2 group-hover:text-[#D4AF37] transition-colors">
                        {d.title}
                      </h4>
                      <p className="text-xs text-[#F4E8C1]/60 leading-relaxed font-light">
                        {d.text}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Press */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Newspaper size={20} className="text-[#D4AF37]" />
              <h3 className="font-display text-xl font-semibold text-[#F4E8C1] tracking-wide uppercase">
                Ils parlent de nous
              </h3>
            </div>
            <div className="space-y-4">
              {PRESS.map((p) => (
                <article
                  key={p.title}
                  className="bg-[#12121a]/60 backdrop-blur-sm border border-[#D4AF37]/20 p-5 hover:border-[#D4AF37]/50 transition-colors duration-400"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <Quote size={18} className="text-[#D4AF37] flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="font-display text-sm font-semibold text-[#D4AF37] uppercase tracking-[0.15em]">
                          {p.source}
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#F4E8C1]/45">
                          {p.year}
                        </span>
                      </div>
                      <h4 className="font-serif italic text-base text-[#F4E8C1] mb-2 leading-snug">
                        « {p.title} »
                      </h4>
                      <p className="text-xs text-[#F4E8C1]/55 leading-relaxed font-light">
                        {p.excerpt}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>

        {/* Final quote */}
        <div className="mt-16 text-center max-w-3xl mx-auto">
          <div className="text-[#D4AF37] text-3xl mb-3">✦</div>
          <p className="font-serif italic text-lg sm:text-xl text-[#F4E8C1]/85 leading-relaxed">
            « La véritable distinction n'est pas un trophée. C'est le jour où
            un client revient, vingt ans après, pour faire raccommoder le sac
            que son père lui a offert. »
          </p>
          <p className="mt-5 text-xs uppercase tracking-[0.3em] text-[#D4AF37]">
            — S.E. MATAR, Maître Artisan
          </p>
        </div>
      </div>
    </section>
  );
}
